/**
 * Paul — Daily Audit Status Box
 *
 * Collapsed status box for the top of the Eddie Gantt Dashboard.
 * Partial mode: 4/6 dimensions active (no Camera, no Brain yet).
 * No numeric health score until all 6 dimensions active.
 *
 * Reads from: daily_audits/{today} in Firestore
 */

import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase';

interface AuditStep {
  step: number;
  name: string;
  percentage: number;
  status: string;
}

interface SpecCompliance {
  totalSpecs: number;
  compliant: number;
  nonCompliant: number;
  notBuilt: number;
  builtNoSpec: number;
  score: number;
  gaps: Array<{ spec: string; requirement: string; status: string }>;
}

interface DeployIntegrity {
  clean: boolean;
  repoHead: string;
  buildHash: string;
  hashMatch: boolean;
  functionsMatch: boolean;
  rulesMatch: boolean;
}

interface CalibrationDelta {
  item: string;
  eddieSaid: number;
  auditFound: number;
  delta: number;
  writtenBackTo: string;
}

interface AuditData {
  date: string;
  auditMode: 'partial' | 'full';
  dimensionsActive: number;
  activeDimensions: string[];
  healthColor: 'green' | 'yellow' | 'red' | 'gray';
  issueCount: number;
  overallStatus: string;
  promise: {
    steps: AuditStep[];
    overallPromise: number;
  };
  specCompliance: SpecCompliance;
  deployIntegrity: DeployIntegrity;
  eddieCalibration: {
    activeDimensions: string[];
    itemsChecked: number;
    recalibrated: number;
    accuracy: number | null;
    deltas: CalibrationDelta[];
  };
}

const COLOR_MAP: Record<string, { bg: string; border: string; text: string }> = {
  green: { bg: 'rgba(22, 163, 74, 0.08)', border: 'rgba(22, 163, 74, 0.25)', text: '#16A34A' },
  yellow: { bg: 'rgba(202, 138, 4, 0.08)', border: 'rgba(202, 138, 4, 0.25)', text: '#CA8A04' },
  red: { bg: 'rgba(220, 38, 38, 0.08)', border: 'rgba(220, 38, 38, 0.25)', text: '#DC2626' },
  gray: { bg: '#F4F4F5', border: '#D4D4D8', text: '#71717A' },
};

export function AuditStatusBox() {
  const [audit, setAudit] = useState<AuditData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    getDoc(doc(db, 'daily_audits', today))
      .then(snap => {
        if (snap.exists()) {
          setAudit(snap.data() as AuditData);
        }
      })
      .catch(() => { /* no audit yet today */ })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={styles.box}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: '#71717A' }}>
          Loading audit...
        </span>
      </div>
    );
  }

  if (!audit) {
    return (
      <div style={{ ...styles.box, background: '#F4F4F5', borderColor: '#D4D4D8' }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: '#71717A' }}>
          DAILY AUDIT — No audit data for today. Run: node functions/daily-audit.js
        </span>
      </div>
    );
  }

  const colors = COLOR_MAP[audit.healthColor] || COLOR_MAP.gray;
  const dateStr = new Date(audit.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div>
      {/* Collapsed box */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          ...styles.box,
          background: colors.bg,
          borderColor: colors.border,
          cursor: 'pointer',
        }}
      >
        <div style={styles.row}>
          <span style={{ ...styles.mono, color: colors.text, fontWeight: 600 }}>
            DAILY AUDIT · {dateStr.toUpperCase()}
          </span>
          <span style={{ ...styles.mono, color: '#18181B' }}>
            {audit.dimensionsActive}/6 DIMENSIONS
          </span>
          <span style={{ ...styles.mono, color: audit.issueCount > 0 ? '#DC2626' : '#16A34A' }}>
            {audit.issueCount} {audit.issueCount === 1 ? 'ISSUE' : 'ISSUES'}
          </span>
          <span style={{ ...styles.mono, color: '#71717A', fontSize: 13 }}>
            {expanded ? '▲' : '▼'}
          </span>
        </div>
        <div style={{ ...styles.mono, color: '#71717A', fontSize: 13, marginTop: 2 }}>
          Score available when all 6 active · Partial mode: promise · spec · deploy · calibration
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={styles.expandedContainer}>
          {/* Promise vs Built */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>PROMISE vs BUILT — {audit.promise.overallPromise}%</div>
            {audit.promise.steps.map(s => (
              <div key={s.step} style={styles.stepRow}>
                <span style={{ ...styles.mono, width: 18 }}>
                  {s.percentage >= 90 ? '✓' : s.percentage >= 50 ? '⚠' : '✗'}
                </span>
                <span style={{ ...styles.mono, flex: 1 }}>
                  {s.step}. {s.name}
                </span>
                <div style={styles.barContainer}>
                  <div style={{
                    ...styles.bar,
                    width: `${s.percentage}%`,
                    background: s.percentage >= 90 ? '#16A34A' : s.percentage >= 50 ? '#CA8A04' : '#DC2626',
                  }} />
                </div>
                <span style={{ ...styles.mono, width: 40, textAlign: 'right' }}>
                  {s.percentage}%
                </span>
              </div>
            ))}
          </div>

          {/* Spec Compliance */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              SPEC COMPLIANCE — {audit.specCompliance.totalSpecs} specs · {audit.specCompliance.score}%
            </div>
            <div style={{ ...styles.mono, fontSize: 13, color: '#52525B' }}>
              Compliant: {audit.specCompliance.compliant} · Non-compliant: {audit.specCompliance.nonCompliant} · Not built: {audit.specCompliance.notBuilt} · Built-no-spec: {audit.specCompliance.builtNoSpec}
            </div>
            {audit.specCompliance.gaps.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {audit.specCompliance.gaps.slice(0, 5).map((g, i) => (
                  <div key={i} style={{ ...styles.mono, fontSize: 13, color: '#DC2626', marginTop: 2 }}>
                    ✗ {g.spec}: {g.requirement} [{g.status}]
                  </div>
                ))}
                {audit.specCompliance.gaps.length > 5 && (
                  <div style={{ ...styles.mono, fontSize: 13, color: '#71717A', marginTop: 2 }}>
                    +{audit.specCompliance.gaps.length - 5} more gaps
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Deploy Integrity */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              DEPLOY INTEGRITY — {audit.deployIntegrity.clean ? '✓ CLEAN' : '✗ DRIFT'}
            </div>
            <div style={{ ...styles.mono, fontSize: 13, color: '#52525B' }}>
              Repo HEAD: {audit.deployIntegrity.repoHead} · Build: {audit.deployIntegrity.buildHash}
              {audit.deployIntegrity.hashMatch ? ' ← matches' : ' ← MISMATCH'}
            </div>
            <div style={{ ...styles.mono, fontSize: 13, color: '#52525B' }}>
              Functions: {audit.deployIntegrity.functionsMatch ? '✓' : '✗'} · Rules: {audit.deployIntegrity.rulesMatch ? '✓' : '✗'}
            </div>
          </div>

          {/* Eddie Calibration */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              EDDIE CALIBRATION — {audit.eddieCalibration.recalibrated} recalibrated
            </div>
            <div style={{ ...styles.mono, fontSize: 13, color: '#52525B' }}>
              Items checked: {audit.eddieCalibration.itemsChecked} · Accuracy: {audit.eddieCalibration.accuracy !== null ? `${audit.eddieCalibration.accuracy}%` : 'insufficient data'}
            </div>
            {audit.eddieCalibration.deltas.slice(0, 5).map((d, i) => (
              <div key={i} style={{ ...styles.mono, fontSize: 13, color: '#52525B', marginTop: 2 }}>
                {d.item}: Eddie={d.eddieSaid}% → Audit={d.auditFound}% (Δ{d.delta > 0 ? '+' : ''}{d.delta}%)
              </div>
            ))}
          </div>

          {/* Inactive Dimensions */}
          <div style={{ ...styles.section, background: '#F4F4F5' }}>
            <div style={{ ...styles.mono, fontSize: 13, color: '#71717A' }}>
              PENDING: Visual Integrity (Camera) · Brain Integrity — activate when systems wired
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  box: {
    padding: '10px 16px',
    borderRadius: 4,
    border: '1px solid #D4D4D8',
    background: '#F4F4F5',
    marginBottom: 12,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    flexWrap: 'wrap' as const,
  },
  mono: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 13,
    letterSpacing: '0.02em',
  },
  expandedContainer: {
    border: '1px solid #D4D4D8',
    borderRadius: 4,
    background: '#FFFFFF',
    padding: 16,
    marginBottom: 12,
  },
  section: {
    padding: '12px 0',
    borderBottom: '1px solid #E4E4E7',
  },
  sectionTitle: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: '0.05em',
    color: '#18181B',
    marginBottom: 8,
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '3px 0',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 13,
    color: '#18181B',
  },
  barContainer: {
    width: 100,
    height: 6,
    background: '#E4E4E7',
    borderRadius: 3,
    overflow: 'hidden' as const,
  },
  bar: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.3s ease',
  },
};
